/** @jsx onetext.jsx.element */

import type { OneText } from '../../../global';

// eslint-disable-next-line no-restricted-syntax
import { BrandLogoWhite as BrandLogo, SpooktacularBackground as BrandBackground } from '../components';
// eslint-disable-next-line no-restricted-syntax
import { defaultBrandConfig, SUPPRESSED_ROUTES } from '../config';
// eslint-disable-next-line no-restricted-syntax
import { HalloweenGraphic as HeroGraphic, SpooktacularSavingsScript } from '../images';

import type { GetPopupAPIResponse, PopupConfig } from '@onetext/api';
import { POPUP_LAYOUT } from '@onetext/api';

declare let onetext : OneText;

const { clsx, deepmerge } = onetext.jsx;

const {
    Popup,
    Page,
    EmailField,
    PhoneField,
    Button,
    Row,
    Columns,
    Column,
    Quiz,
    QuizOption,
    Terms,
    Teaser,
    PopupContainer
} = onetext.component.consent.popup;

const DISCOUNT = '20%';

const customBrandConfig : PopupConfig = {
    style: {
        color: {
            text:       '#ffffff',
            background: 'rgba(30,32,31,0.97)'
        }
    },
    component: {
        button: {
            level: {
                primary: {
                    style: {
                        text: {
                            base:  '#ffffff',
                            hover: '#000000'
                        },
                        background: {
                            base:  'rgba(109, 192, 100, 1.00)',
                            hover: '#ffffff'
                        }
                    }
                }
            }
        }
    }
};

type HalloweenSpooktacularPopupProps = {
    popup : GetPopupAPIResponse,
};

export const HalloweenSpooktacularPopup = ({
    popup
} : HalloweenSpooktacularPopupProps) : JSX.Element | null => {
    const brandConfig = deepmerge(defaultBrandConfig, customBrandConfig);
    return (
        <PopupContainer
            popup={ { ...popup, config: brandConfig } }
            suppressedRoutes={ SUPPRESSED_ROUTES }>
            <Popup
                layout={ POPUP_LAYOUT.FULLSCREEN }
                background={ <BrandBackground /> }>
                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-lg',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'mb-4'
                                    )
                                }>
                                    <img
                                        src={ SpooktacularSavingsScript }
                                        alt={ 'Spooktacular Savings' }
                                        className={ 'h-[48px]' } />
                                </p>
                                <p className={
                                    clsx(
                                        'text-7xl md:text-8xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-bold'
                                    )
                                }>
                                    { `${ DISCOUNT } off` }
                                </p>
                                <p className={
                                    clsx(
                                        'text-xl md:text-2xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-light',
                                        'text-[#FBC986]'
                                    )
                                }>
                                    { 'Your entire order!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-4 mb-6',
                                        'w-[280px]'
                                    )
                                }>
                                    { 'To claim your discount, tell us the age you\'re shopping for today:' }
                                </p>
                                <Quiz propertyName={ 'shopping_age' }>
                                    <div className={
                                        clsx(
                                            'w-[264px]',
                                            'flex flex-col',
                                            'items-center',
                                            'gap-2.5'
                                        )
                                    }>
                                        <QuizOption propertyValue={ 'newborn' }>
                                            <Button>
                                                { 'Newborn' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '6m_24m' }>
                                            <Button>
                                                { '6M - 24M' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '2t_4t' }>
                                            <Button>
                                                { '2T - 4T' }
                                            </Button>
                                        </QuizOption>
                                        <QuizOption propertyValue={ '5_years_plus' }>
                                            <Button>
                                                { '5 YEARS+' }
                                            </Button>
                                        </QuizOption>
                                    </div>
                                </Quiz>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <div
                                    className={
                                        clsx(
                                            'w-[320px] h-[440px]',
                                            'rounded-[8px]',
                                            'bg-center bg-cover bg-no-repeat',
                                            'fade-in-up'
                                        )
                                    }
                                    style={ { backgroundImage: `url(${ HeroGraphic })` } } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-lg',
                                        'font-normal',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'mb-4'
                                    )
                                }>
                                    <img
                                        src={ SpooktacularSavingsScript }
                                        alt={ 'Spooktacular Savings' }
                                        className={ 'h-[48px]' } />
                                </p>
                                <p className={
                                    clsx(
                                        'text-7xl md:text-8xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-bold'
                                    )
                                }>
                                    { `${ DISCOUNT } off` }
                                </p>
                                <p className={
                                    clsx(
                                        'text-xl md:text-2xl',
                                        'whitespace-nowrap',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-light',
                                        'text-[#FBC986]'
                                    )
                                }>
                                    { 'Your entire order!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-4 mb-6',
                                        'w-[280px]'
                                    )
                                }>
                                    { `Sign up with your email address to claim your ${ DISCOUNT } discount` }
                                </p>
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5'
                                    )
                                }>
                                    <EmailField autofocus={ true } />
                                    <Button>
                                        { 'Continue' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <div
                                    className={
                                        clsx(
                                            'w-[320px] h-[440px]',
                                            'rounded-[8px]',
                                            'bg-center bg-cover bg-no-repeat',
                                            'fade-in-up'
                                        )
                                    }
                                    style={ { backgroundImage: `url(${ HeroGraphic })` } } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-[40px]',
                                        'font-semibold',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'Finish signing up' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mt-3 mb-6',
                                        'w-[280px]',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { `with text to activate your ${ DISCOUNT } off discount` }
                                </p>
                                <Terms
                                    brandName={ 'Caden Lane' }
                                    termsUrl={ 'https://cadenlane.com/Pages/terms-of-service' }
                                    privacyUrl={ 'https://cadenlane.com/Pages/privacy-policy' }
                                />
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5',
                                        'mt-8'
                                    )
                                }>
                                    <PhoneField autofocus={ true } />
                                    <Button>
                                        { `Activate ${ DISCOUNT } Off` }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <div
                                    className={
                                        clsx(
                                            'w-[320px] h-[440px]',
                                            'rounded-[8px]',
                                            'bg-center bg-cover bg-no-repeat',
                                            'fade-in-up'
                                        )
                                    }
                                    style={ { backgroundImage: `url(${ HeroGraphic })` } } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>

                <Page>
                    <Row>
                        <Columns className={ 'gap-24' }>
                            <Column>
                                <div className={ 'mb-10' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-7xl',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'font-semibold',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'You\'re in!' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-2xl',
                                        'font-light',
                                        'tracking-wider',
                                        'text-center',
                                        'w-[280px]',
                                        'mt-3 mb-6'
                                    )
                                }>
                                    { 'Use this code at checkout:' }
                                </p>
                                <div className={
                                    clsx(
                                        'flex flex-col items-center justify-center',
                                        'bg-[rgba(0,0,0,0.15)]',
                                        'border-4 border-dashed border-[#FBC986]',
                                        'text-white fira-sans text-base',
                                        'h-16 px-5 py-[18px]',
                                        'relative z-[1]',
                                        'mb-10'
                                    )
                                }>
                                    <div className={
                                        clsx(
                                            'text-white fira-sans',
                                            'text-xl font-extrabold',
                                            'tracking-[1px] leading-5',
                                            'text-center'
                                        )
                                    }>
                                        { 'SPOOKY20' }
                                    </div>
                                </div>
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex flex-col',
                                        'items-center',
                                        'gap-2.5'
                                    )
                                }>
                                    <Button>
                                        { 'Back to site' }
                                    </Button>
                                </div>
                            </Column>
                            <Column className={ clsx('col-span-1', 'hidden md:flex') }>
                                <div
                                    className={
                                        clsx(
                                            'w-[320px] h-[440px]',
                                            'rounded-[8px]',
                                            'bg-center bg-cover bg-no-repeat',
                                            'fade-in-up'
                                        )
                                    }
                                    style={ { backgroundImage: `url(${ HeroGraphic })` } } />
                            </Column>
                        </Columns>
                    </Row>
                </Page>
            </Popup>
            <Teaser customText={ `Get ${ DISCOUNT } off` } />
        </PopupContainer>
    );
};
